import { GridActionsCellItem } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import CustomStyledTable from "../../../../../components/table/CustomStyledTable";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import { Columns } from "./data";
import { GoUpload } from "react-icons/go";
import DepartmentModalForm from "./modal";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../../components/search/APISearchInput";
import DepartmentLeadership from "./leadership";
import { Popover } from "@mui/material";
import { BsPencil, BsTrash } from "react-icons/bs";
import { LuEye } from "react-icons/lu";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import {
	DepartmentDto,
	QueryDepartmentDto,
} from "../../../../../models/department.dto";
import {
	useGetDepartmentSearch,
	useGetDepartments,
	useDeleteDepartment,
} from "../../../../../services/department.service";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { EmployeeOrderBy } from "../../../../../models/employee.dto";
import { useGetEmployee } from "../../../../../services/employee.service";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../../helpers/usePermissionManager";
import UploadDepartment from "../departments/modal/UploadDepartment";
import { toast } from "react-toastify";
import { getAccessToken } from "../../../../../services/utils/app.data";
import ENDPOINTS from "../../../../../services/utils/endpoints";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import {
	departmentColumns,
	departmentColumnVisibility,
} from "./tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../models/table-configuration.dto";
import ExportDepartmentsModal from "./modal/ExportDepartmentsModal";

const Departments = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openLeadershipModal, setOpenLeadershipModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const [openUploadDepartmentModal, setOpenUploadDepartmentModal] =
		useState(false);
	const [isEditForm, setIsEditForm] = useState(false);

	const { mutate: searchDepartments } = useGetDepartmentSearch(
		currentCompany?.id as number,
	);
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<DepartmentDto>({
		fetchHook: (options: QueryDepartmentDto) =>
			useGetDepartments(currentCompany?.id as number, options),
	});

	// Fetched data for select fields requiring data from database
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteDepartment } = useDeleteDepartment();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteDepartment(
			{
				companyId: currentCompany?.id as number,
				id: formDataValues?.id,
			},
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleOpenExportModal = () => {
		setIsOpen(false);
		setOpenExportModal(true);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	const columnsWithAdditions = Columns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj,
	);

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Departments</h2>
			<hr />
			<div className="flex items-center justify-between mt-7 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchDepartments}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Name",
										fieldName: "name",
									},
									{
										title: "Code",
										fieldName: "code",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Active",
								fieldType: "YES_NO",
								fieldName: "active",
							},
						]}
					/>
				</div>
				<div className="flex items-center mx-2">
					<div className="relative" ref={dropdownRef}>
						{/* Dropdown Trigger Button */}
						<button
							onClick={() => setIsOpen(!isOpen)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<Upload className="mr-3 text-[17px] text-gray-500" />
							Upload/Export
							<ChevronDown
								className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
									isOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{/* Dropdown Menu */}
						{isOpen && (
							<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
								<div className="p-2 space-y-2">
									<PermissionChecker
										permissionRule="OR"
										permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
									>
										<button
											onClick={() => setOpenUploadDepartmentModal(true)}
											className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
									   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
										>
											<GoUpload className="text-[18px] text-gray-500" />
											Upload Departments
										</button>
									</PermissionChecker>
									<button
										onClick={handleOpenExportModal}
										className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
									 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
									>
										<BsDownload className="text-[18px] text-gray-500" />
										Export Departments
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
				<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
					<div
						className="flex items-center text-primary-blue text-sm cursor-pointer w-fit"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-sm" />
						Add New Department
					</div>
				</PermissionChecker>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.DEPARTMENTS}
				data={tableData}
				columns={departmentColumns}
				loading={isLoading}
				onRowClick={
					checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
						? (row, event) => {
								setFormData(row);
								setAnchorEl(event.currentTarget);
							}
						: undefined
				}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={departmentColumnVisibility}
				pageSize={10}
				hasPermission={checkUserPermissionsAnd([
					PERMISSIONS.COMPANY_CONFIGS_READ,
				])}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={
					checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
						? setAnchorEl
						: undefined
				}
				hasPermission={checkUserPermissionsAnd([
					PERMISSIONS.COMPANY_CONFIGS_READ,
				])}
			/> */}
			<Popover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: "auto",
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						onClick={() => {
							setIsEditForm(true);
							setOpenModal(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<BsPencil />
						<p className="text-2sm">Edit</p>
					</div>
					<div
						onClick={() => {
							setOpenLeadershipModal(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<LuEye />
						<p className="text-2sm">View Leadership</p>
					</div>
					<div
						onClick={() => {
							handleClose();
							setOpenDeleteModal(true);
						}}
						className="table-action-popover"
					>
						<BsTrash />
						<p className="text-2sm">Delete</p>
					</div>
				</div>
			</Popover>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<DepartmentModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Department" : "Add New Department"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					reloadTableData={reloadTableData}
				/>
			)}
			{openLeadershipModal && (
				<DepartmentLeadership
					open={openLeadershipModal}
					setOpen={setOpenLeadershipModal}
					employees={employees?.data || []}
				/>
			)}
			{openUploadDepartmentModal && (
				<UploadDepartment
					open={openUploadDepartmentModal}
					setOpen={setOpenUploadDepartmentModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportDepartmentsModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</div>
	);
};

export default Departments;
